import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  X, 
  AlertTriangle, 
  Printer,
  Package,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';

interface ReportIssueDialogProps {
  isOpen: boolean;
  onClose: () => void;
  assetId?: string;
  assetName?: string;
  assetTag?: string;
  assetCategory?: string;
}

interface IssueFormData {
  assetId: string;
  categoryId: string;
  priority: string;
  subject: string;
  description: string;
}

interface CompatibleToner {
  id: string;
  name: string;
  color?: string;
  partNumber?: string;
  totalStock: number;
  stockByBranch?: Array<{
    branchId: string;
    branchName: string;
    quantity: number;
  }>;
}

interface TicketCategory {
  id: string;
  name: string;
  description?: string;
}

const PRIORITIES = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'URGENT', label: 'Urgent' },
  { value: 'CRITICAL', label: 'Critical' },
];

export default function ReportIssueDialog({
  isOpen,
  onClose,
  assetId,
  assetName,
  assetTag,
  assetCategory,
}: ReportIssueDialogProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [formData, setFormData] = useState<IssueFormData>({
    assetId: assetId || '',
    categoryId: '',
    priority: 'MEDIUM',
    subject: '',
    description: '',
  });

  const [selectedPrinterId, setSelectedPrinterId] = useState<string>(assetId || '');

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        assetId: assetId || '',
        categoryId: '',
        priority: 'MEDIUM',
        subject: '',
        description: '',
      });
      setSelectedPrinterId(assetId || '');
    }
  }, [isOpen, assetId]);

  // Fetch ticket categories
  const { data: categoriesData } = useQuery({
    queryKey: ['ticket-categories'],
    queryFn: async () => {
      const response = await api.get('/tickets/public/categories');
      return response.data;
    },
    enabled: isOpen,
  });

  // Fetch printer assets for dropdown (if no assetId provided)
  const { data: printerAssetsData } = useQuery({
    queryKey: ['printer-assets-for-issue'],
    queryFn: async () => {
      const response = await api.get('/assets?category=PRINTERS&limit=500');
      return response.data;
    },
    enabled: isOpen && !assetId,
  });

  // Fetch compatible toners when a printer is selected
  const { data: compatibleTonersData, isLoading: tonersLoading } = useQuery({
    queryKey: ['compatible-toners-for-issue', selectedPrinterId],
    queryFn: async () => {
      const response = await api.get(`/consumables/printer/${selectedPrinterId}/compatible-toners`);
      return response.data;
    },
    enabled: isOpen && !!selectedPrinterId && (assetCategory === 'PRINTERS' || !assetId),
  });

  const ticketCategories: TicketCategory[] = categoriesData?.data || [];
  const printerAssets = printerAssetsData?.data || [];
  const compatibleToners: CompatibleToner[] = compatibleTonersData?.data?.compatibleToners || [];
  const printerInfo = compatibleTonersData?.data?.printer;

  // Submit ticket mutation
  const submitIssueMutation = useMutation({
    mutationFn: async (data: IssueFormData) => {
      return api.post('/tickets', {
        subject: data.subject,
        description: data.description,
        categoryId: data.categoryId,
        priority: data.priority,
        assetId: data.assetId || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      toast({
        title: 'Success',
        description: 'Issue reported successfully',
      });
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to report issue',
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitIssueMutation.mutate({
      ...formData,
      assetId: selectedPrinterId || formData.assetId,
    });
  };

  const getColorBadge = (color?: string) => {
    if (!color || color === 'na') return null;
    
    const colorStyles: Record<string, string> = {
      black: 'bg-gray-900 text-white',
      cyan: 'bg-cyan-500 text-white',
      magenta: 'bg-pink-500 text-white',
      yellow: 'bg-yellow-400 text-black',
    };
    
    return (
      <span className={cn(
        'px-2 py-0.5 text-xs font-medium rounded-full capitalize',
        colorStyles[color.toLowerCase()] || 'bg-gray-200 text-gray-800'
      )}>
        {color}
      </span>
    );
  };

  const getStockStatus = (quantity: number) => {
    if (quantity === 0) return { color: 'text-red-600 bg-red-50', label: 'Out of Stock' };
    if (quantity <= 2) return { color: 'text-yellow-600 bg-yellow-50', label: 'Low Stock' };
    return { color: 'text-green-600 bg-green-50', label: 'In Stock' };
  };

  if (!isOpen) return null;

  const isPrinterCategory = assetCategory === 'PRINTERS' || 
    (selectedPrinterId && printerAssets.find((p: any) => p.id === selectedPrinterId)?.category?.name?.includes('PRINTER'));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto">
      <Card className="w-full max-w-lg mx-4 my-8 max-h-[90vh] overflow-y-auto">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-xl font-semibold">Report Issue</h2>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Asset Selection (if not provided) */}
            {!assetId ? (
              <div>
                <Label htmlFor="asset">Select Printer *</Label>
                <select
                  id="asset"
                  value={selectedPrinterId}
                  onChange={(e) => {
                    setSelectedPrinterId(e.target.value);
                    setFormData({ ...formData, assetId: e.target.value });
                  }}
                  required
                  className="w-full h-10 px-3 py-2 mt-1 rounded-md border border-input bg-background text-sm"
                >
                  <option value="">Select a printer...</option>
                  {printerAssets.map((printer: any) => (
                    <option key={printer.id} value={printer.id}>
                      {printer.assetTag} - {printer.name || printer.model} ({printer.branch?.name || 'No Branch'})
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="p-3 bg-muted rounded-md">
                <p className="text-sm text-muted-foreground">Asset</p>
                <p className="font-medium">
                  <span className="font-mono">{assetTag}</span> - {assetName}
                </p>
              </div>
            )}

            {/* Compatible Toners Section - Show when printer is selected */}
            {selectedPrinterId && isPrinterCategory && (
              <div className="border rounded-lg p-4 bg-blue-50/50">
                <div className="flex items-center gap-2 mb-3">
                  <Package className="w-4 h-4 text-blue-600" />
                  <Label className="font-semibold text-blue-900">Compatible Toners</Label>
                </div>

                {tonersLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    <span className="ml-2 text-sm text-blue-600">Loading toners...</span>
                  </div>
                ) : compatibleToners.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground mb-2">
                      {printerInfo?.assetTag && `Toners for ${printerInfo.assetTag}`}
                      {printerInfo?.branch?.name && ` (${printerInfo.branch.name})`}
                    </p>
                    <div className="grid gap-2">
                      {compatibleToners.map((toner) => {
                        const stockStatus = getStockStatus(toner.totalStock);
                        return (
                          <div
                            key={toner.id}
                            className={cn(
                              'flex items-center justify-between p-3 rounded-lg border bg-white',
                              toner.totalStock === 0 && 'border-red-200',
                              toner.totalStock > 0 && toner.totalStock <= 2 && 'border-yellow-200'
                            )}
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex flex-col">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm">{toner.name}</span>
                                  {getColorBadge(toner.color)}
                                </div>
                                {toner.partNumber && (
                                  <span className="text-xs text-muted-foreground font-mono">
                                    {toner.partNumber}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={cn(
                                'px-2 py-1 rounded-md text-sm font-medium',
                                stockStatus.color
                              )}>
                                Qty: {toner.totalStock}
                              </span>
                              {toner.totalStock === 0 && (
                                <AlertCircle className="w-4 h-4 text-red-500" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Stock Summary */}
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Total toner types:</span>
                        <span className="font-medium">{compatibleToners.length}</span>
                      </div>
                      <div className="flex justify-between text-xs mt-1">
                        <span className="text-muted-foreground">Out of stock:</span>
                        <span className={cn(
                          'font-medium',
                          compatibleToners.filter(t => t.totalStock === 0).length > 0 ? 'text-red-600' : 'text-green-600'
                        )}>
                          {compatibleToners.filter(t => t.totalStock === 0).length}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Printer className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">
                      No compatible toners found for this printer.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Configure compatible toner categories for this printer.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Issue Type / Category */}
            <div>
              <Label htmlFor="categoryId">Issue Category *</Label>
              <select
                id="categoryId"
                value={formData.categoryId}
                onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                required
                className="w-full h-10 px-3 py-2 mt-1 rounded-md border border-input bg-background text-sm"
              >
                <option value="">Select issue category...</option>
                {ticketCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Priority */}
            <div>
              <Label htmlFor="priority">Priority *</Label>
              <select
                id="priority"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                required
                className="w-full h-10 px-3 py-2 mt-1 rounded-md border border-input bg-background text-sm"
              >
                {PRIORITIES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Subject */}
            <div>
              <Label htmlFor="subject">Subject *</Label>
              <input
                id="subject"
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                required
                className="w-full h-10 px-3 py-2 mt-1 rounded-md border border-input bg-background text-sm"
                placeholder="Brief summary of the issue..."
                maxLength={200}
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Description *</Label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                className="w-full min-h-[100px] px-3 py-2 mt-1 rounded-md border border-input bg-background text-sm"
                placeholder="Describe the issue in detail..."
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitIssueMutation.isPending || !formData.categoryId || !formData.subject || !formData.description}
              >
                {submitIssueMutation.isPending ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting...
                  </span>
                ) : (
                  <>
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Report Issue
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
